import * as pc from "playcanvas";
import type { GameClient, MatchHudState } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

const BEEP_URL = "/audio/buy-beep.mp3";

/**
 * CountdownBeep — a 2D UI beep on each of the last 3 seconds of the buy
 * phase (3 → 2 → 1), CS:GO freezetime style.
 *
 * Anchors to the server's `buySecondsLeft` and ticks down on the wall
 * clock so the beep lands on the second mark regardless of snapshot
 * cadence. Each second fires at most once per buy phase.
 */
export class CountdownBeep {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly emitter: pc.Entity;
  private ready = false;
  private phase = "";
  private anchorSec = 0;
  private anchorMs = 0;
  private readonly beeped = new Set<number>();
  private readonly unsubs: Array<() => void> = [];
  private readonly tickFn: () => void;

  constructor(app: pc.AppBase, client: GameClient) {
    this.app = app;
    this.client = client;

    this.emitter = new pc.Entity("buy-beep");
    this.app.root.addChild(this.emitter);

    this.app.assets.loadFromUrl(BEEP_URL, "audio", (err, asset) => {
      if (err || !asset) {
        console.warn("[CountdownBeep] failed to load beep:", BEEP_URL, err);
        return;
      }
      this.emitter.addComponent("sound");
      const s = this.emitter.sound;
      if (s) {
        s.positional = false; // 2D UI sound
        s.addSlot("beep", {
          asset: asset.id, volume: 0.5, loop: false, autoPlay: false, overlap: true,
        });
      }
      this.ready = true;
    });

    unlockAudioOnGesture(this.app);
    this.unsubs.push(this.client.onMatchHud((hud) => this.onHud(hud)));

    this.tickFn = () => this.update();
    this.app.on("update", this.tickFn);
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    for (const u of this.unsubs) u();
    this.emitter.destroy();
  }

  private onHud(hud: MatchHudState): void {
    if (hud.phase !== this.phase) {
      this.phase = hud.phase;
      this.beeped.clear(); // new phase — re-arm all three beeps
    }
    if (hud.phase === "buy" && typeof hud.buySecondsLeft === "number") {
      this.anchorSec = hud.buySecondsLeft;
      this.anchorMs = performance.now();
    }
  }

  private update(): void {
    if (!this.ready || this.phase !== "buy") return;
    const remaining = this.anchorSec - (performance.now() - this.anchorMs) / 1000;
    const sec = Math.ceil(remaining);
    if (sec >= 1 && sec <= 3 && !this.beeped.has(sec)) {
      this.beeped.add(sec);
      resumeAudioContext(this.app);
      this.emitter.sound?.play("beep");
    }
  }
}
